if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        // Auto-load and play the first video when component is connected to DOM
        this.autoplayFirstVideo();
      }

      autoplayFirstVideo() {
        // Find the first active media item (or the first media item if none is active)
        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]') || 
                           this.elements.viewer.querySelector('[data-media-id]');
        
        if (!activeMedia) return;

        // Check if it contains a video
        const deferredMedia = activeMedia.querySelector('.deferred-media');
        if (deferredMedia) {
          // Wait a short moment for the page to fully load
          setTimeout(() => {
            // Load the video content
            if (deferredMedia.loadContent) {
              deferredMedia.loadContent(false);
              
              // For videos, ensure they start playing
              setTimeout(() => {
                const video = deferredMedia.querySelector('video');
                if (video) {
                  video.muted = true; // Ensure muted for autoplay
                  video.play().catch(e => {
                    console.log('Autoplay prevented:', e);
                  });
                }
              }, 500); // Give time for video to load
            }
          }, 100);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        
        // Auto-play video when slide changes
        const activeMedia = event.detail.currentElement;
        if (activeMedia) {
          this.playActiveMedia(activeMedia);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);
          
          // Ensure video autoplays after loading
          setTimeout(() => {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.muted = true; // Ensure muted for autoplay
              video.play().catch(e => {
                console.log('Video autoplay prevented:', e);
              });
            }
          }, 500);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Auto-load videos on all product pages when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Find all deferred video media on the page
  const deferredVideos = document.querySelectorAll('.product__media-item.is-active .deferred-media, .product__media-item:first-child .deferred-media');
  
  deferredVideos.forEach((deferredMedia) => {
    if (deferredMedia.loadContent) {
      // Load the video
      deferredMedia.loadContent(false);
      
      // Ensure it plays
      setTimeout(() => {
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.play().catch(e => {
            console.log('Autoplay prevented:', e);
          });
        }
      }, 500);
    }
  });
});